import * as React from 'react';
import {useDispatch} from 'react-redux';

import {UpgradeArrow} from 'shells/limits/upgrade-arrow';
import {StatusBadge} from 'shells/status-badge/status-badge';

import type {LimitedType} from 'nutshell-graphql-types';

import {openModalForType} from './limits-actions';
import {LimitsWarning} from './limits-warning';
import {useGetLimitsByType} from './use-get-limits-by-type';

import styles from './title-bar-limits.module.css';

type Props = {
    type: LimitedType;
    onClick?: () => void;
    showTrialUpgradeArrow?: boolean;
};

export function TitleBarLimits(props: Props) {
    const dispatch = useDispatch();
    const {limits} = useGetLimitsByType(props.type);

    if (limits && limits.isEnforced) {
        const currentUsage = limits.currentUsage;
        const planLimit = limits.currentPlanLimit;
        const typeString = limits.humanReadableType;
        const tierPlanLimit = limits.tierPlanLimit;
        const tierPlanLimitValidated = typeof tierPlanLimit === 'number' ? tierPlanLimit : '∞';

        const handleClick = () => {
            if (props.onClick) {
                props.onClick();
            } else {
                dispatch(openModalForType(props.type));
            }
        };

        const limitWarning = props.showTrialUpgradeArrow ? (
            <div data-tip='Your usage is above some plan’s limits.' className='pt-4'>
                <UpgradeArrow featureName={typeString} onClick={handleClick} />
            </div>
        ) : (
            <LimitsWarning />
        );

        return (
            <>
                {NutClientConfig.shouldShowTrialPlanLimits && limits.shouldShowWarningInTrial ? (
                    limitWarning
                ) : (
                    <div onClick={handleClick} className={styles['title-bar-limits']}>
                        <StatusBadge
                            level={planLimit ? getLevel(currentUsage, planLimit) : 'info--grey'}
                            text={`${currentUsage} / ${tierPlanLimitValidated}`}
                            detail={`You’re using ${currentUsage} of your ${
                                typeof tierPlanLimit === 'number' ? tierPlanLimit : 'unlimited'
                            } ${typeString}`}
                        />
                    </div>
                )}
            </>
        );
    }

    return null;
}

function getLevel(currentUsage: number, planLimit: number) {
    if (currentUsage / planLimit >= 1) {
        return 'alert';
    } else if (currentUsage / planLimit > 0.9) {
        return 'warning';
    } else {
        return 'info--grey';
    }
}
